import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress, { CircularProgressProps } from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useEffect } from 'react';

interface CircularProgressWithLabelProps extends CircularProgressProps {
  value: number;
  textColor: string;
  circleColor: string;
  size?: number;
  text?: string;
  thickness?: number;
}

interface AutoCircularProgressWithLabelProps extends Omit<CircularProgressWithLabelProps, 'value'> {
  config: { delay: number; increment: number };
  onComplete?: () => void;
}

const TextProgressContainer = styled(Box)({
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  paddingLeft: 20,
  paddingRight: 20,
});

const CircularProgressWithLabel: React.FC<CircularProgressWithLabelProps> = (props) => {
  const { textColor, circleColor, size = 40, thickness = 4, ...rest } = props;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        sx={{
          color: circleColor,
        }}
        size={size}
        thickness={thickness}
        {...rest}
        value={100}
      />
      <CircularProgress
        variant="determinate"
        sx={{
          color: '#0e0255',
          position: 'absolute',
          left: 0,
        }}
        size={size}
        thickness={thickness}
        value={props.value}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute !important',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {props.text ? (
          <TextProgressContainer>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#0e0255', mb: 0.5 }}>
              {props.text}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: textColor, fontSize: '0.60rem', fontWeight: '600' }}
            >{`${Math.round(props.value)}%`}</Typography>
          </TextProgressContainer>
        ) : (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: textColor, fontSize: '0.60rem', fontWeight: '600' }}
          >{`${Math.round(props.value)}%`}</Typography>
        )}
      </Box>
    </Box>
  );
};

export const AutoCircularProgressWithLabel: React.FC<AutoCircularProgressWithLabelProps> = ({
  config,
  onComplete,
  ...rest
}) => {
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + config.increment;
      });
    }, config.delay);

    return () => {
      clearInterval(interval);
    };
  }, [config, setProgress, onComplete]);

  useEffect(() => {
    if (progress === 100 && onComplete) {
      setTimeout(() => {
        onComplete();
      }, 500);
    }
  }, [progress, onComplete]);

  return <CircularProgressWithLabel value={progress} {...rest} />;
};

export default CircularProgressWithLabel;
